import { makeAutoObservable, runInAction } from "mobx";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import type RootStore from "stores/RootStore";
import { isOperator } from "auth/tier";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  DEVOPS_SUBSCRIPTION_EVENT_TYPES,
  type DevOpsConnection,
  type DevOpsProject,
  type DevOpsSubscription,
  type DevOpsSubscriptionCreatePayload,
  type DevOpsSubscriptionFormData,
} from "../devops.types";
import type DevOpsScopeStore from "../shared/DevOpsScopeStore";
import { swallowedFailure } from "../shared/swallowedFind";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

export type DevOpsSubscriptionSortKey =
  | "eventType"
  | "status"
  | "projectScope"
  | "createdAt";

/** Sort keys that read best newest-first on first click. */
const DESC_FIRST: ReadonlySet<DevOpsSubscriptionSortKey> = new Set([
  "createdAt",
]);

/** The friendly label the grid shows for an event type (falls back to the raw value). */
const eventTypeLabel = (eventType: string): string =>
  DEVOPS_SUBSCRIPTION_EVENT_TYPES.find((e) => e.value === eventType)?.label ||
  eventType;

/**
 * Normalise a ServiceStore `find()` response into a plain array of rows.
 * Paginated services return `{ data, total }`; custom non-paginated finds may
 * return the bare array — tolerate both. (Replaces the removed `unwrapList`.)
 */
function rowsOf<T>(result: { data?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/**
 * View store for the DevOps service-hook Subscriptions admin view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. Backed by registered `@intrasync/stores` ServiceStores
 * (`devops-connections`, `devops-projects`, `devops-subscriptions`). Reads are
 * viewer+; create / remove are operator/admin — the API enforces the gate
 * (`requireActionAccess`), the UI hides the buttons via {@link canManage}.
 *
 * `connections` is a computed view over the `devops-connections` entity cache.
 * `subscriptions` and `projects` are connection-scoped and re-queried on every
 * connection change, so they are read from each `find()` RESPONSE (the entity
 * cache accumulates across queries and would leak rows from other connections).
 *
 * SECRET HANDLING: the per-subscription shared secret is generated server-side
 * and NEVER returned (records carry only `hasSecret`); nothing secret ever
 * passes through this store.
 */
/**
 * Page size for the subscription read. The API clamps `$limit` to 500 and
 * defaults to 200; it returns a bare array with no `total`, so a full page is
 * the only hint that rows were left behind — see
 * {@link DevOpsSubscriptionsStore.isAtCap}.
 */
export const SUBSCRIPTION_PAGE_SIZE = 200;

/** The "Project Scope" cell for a row whose scope was never recorded. */
export const UNKNOWN_SCOPE_NOTE_LABEL = "unknown";

/**
 * Why that cell says "unknown" (the column's tooltip). The same sentence the
 * Overview card and Project 360 use: a row registered before per-project
 * scope was recorded may cover any project on the connection — it was never
 * verified to be org-wide, and a bare dash would claim it was.
 */
export const UNKNOWN_SCOPE_NOTE =
  "This subscription was registered before per-project scope was recorded, so HQ cannot say which project it covers. Re-create it with a project to scope it.";

class DevOpsSubscriptionsStore {
  rootStore: RootStore;

  subscriptions: DevOpsSubscription[] = [];
  /** Azure projects for the selected connection — powers the dialog's project picker. */
  projects: DevOpsProject[] = [];

  connectionsLoading = false;
  subscriptionsLoading = false;
  projectsLoading = false;

  /**
   * Why the project list is empty, when it is empty because the lookup FAILED
   * rather than because the org has none. The Add Subscription dialog shows it
   * on the project picker; the subscription list itself is unaffected.
   */
  projectsError: string | null = null;
  error: string | null = null;

  /** Bumped per project read; only the newest answer may report anything. */
  private projectsSeq = 0;

  /** The same, for the subscription list this page is actually about. */
  private subscriptionsSeq = 0;

  // Header filter (applied client-side over the loaded subscription list).
  searchQuery = "";

  // Column sort (applied client-side over the filtered rows, like the search)
  sortKey: DevOpsSubscriptionSortKey = "createdAt";
  sortDir: SortDir = "desc";

  // Create form state (shared by the Add Subscription dialog)
  formData: DevOpsSubscriptionFormData = {};
  saving = false;
  formError: string | null = null;

  /** Subscription id with an in-flight remove. */
  removingId: string | null = null;

  /** Subscription pending delete confirmation (drives the confirm dialog). */
  confirmTarget: DevOpsSubscription | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- ServiceStore accessors ----

  private get connectionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-connections") ?? this.rootStore.devopsConnectionsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  private get projectsService(): ServiceStoreType | null {
    return (this.rootStore.devopsProjectsStore as ServiceStoreType | null) ?? null;
  }

  private get subscriptionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-subscriptions") ?? this.rootStore.devopsSubscriptionsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /** Connection list — computed over the `devops-connections` entity cache. */
  get connections(): DevOpsConnection[] {
    return (this.connectionsService?.entities ?? []) as unknown as DevOpsConnection[];
  }

  // ---- The shared scope -----------------------------------------------------

  /**
   * WHICH connection is the shared `devopsScopeStore`'s fact (see
   * shared/DevOpsScopeStore.ts) — this page reads the same selection the
   * boards and Project 360 use, so the operator does not pick it a fifth
   * time. This page has no project selector of its own; the scope's project
   * only seeds the Add Subscription dialog's picker.
   */
  private get scopeStore(): DevOpsScopeStore | null {
    return (this.rootStore.devopsScopeStore as DevOpsScopeStore | undefined) ?? null;
  }

  get selectedConnectionId(): string | null {
    return this.scopeStore?.connectionId ?? null;
  }

  // ---- Derived --------------------------------------------------------------

  /**
   * Whether the current user may create/remove subscriptions (operator/admin).
   * The API is the real enforcement point (`requireActionAccess`); this only
   * governs whether the buttons render. Tier resolution is centralised in
   * `auth/tier.ts` — widen there, never at the call sites.
   */
  get canManage(): boolean {
    return isOperator(this.rootStore);
  }

  get selectedConnection(): DevOpsConnection | null {
    if (!this.selectedConnectionId) return null;
    return (
      this.connections.find((c) => c.id === this.selectedConnectionId) || null
    );
  }

  get activeCount(): number {
    return this.subscriptions.filter((s) => s.status === "active").length;
  }

  get failedCount(): number {
    return this.subscriptions.filter((s) => s.status === "failed").length;
  }

  get pendingCount(): number {
    return this.subscriptions.filter((s) => s.status === "pending").length;
  }

  /** Resolve an Azure project GUID to its name (best-effort, current connection only). */
  projectName(projectId?: string | null): string {
    if (!projectId) return "";
    return this.projects.find((p) => p.id === projectId)?.name || projectId;
  }

  /**
   * The project a row is scoped to: the API's flat `devopsProjectId` column
   * (recorded on create since 2026-09-03), else the `publisherInputs` an
   * older API echoed back. Null = no project scope was recorded, which is an
   * UNKNOWN scope, not a verified org-wide hook.
   */
  scopeProjectId(s: DevOpsSubscription): string | null {
    return s.devopsProjectId ?? s.publisherInputs?.projectId ?? null;
  }

  /**
   * What search / sort read for a row's scope: the name the API stored, else
   * the picker's name for the id, else the raw id, else "" for a row with no
   * recorded scope.
   */
  scopeLabel(s: DevOpsSubscription): string {
    return s.devopsProjectName || this.projectName(this.scopeProjectId(s));
  }

  /**
   * What the "Project Scope" COLUMN renders. A row with no recorded scope is
   * "unknown", never a bare dash: a dash in that column reads as "none / all
   * projects", and this row's scope is simply not known — it may be scoped to
   * some other project. {@link UNKNOWN_SCOPE_NOTE} is the cell's tooltip.
   */
  scopeDisplay(s: DevOpsSubscription): string {
    return this.scopeLabel(s) || UNKNOWN_SCOPE_NOTE_LABEL;
  }

  /** How many subscriptions were loaded for the open connection. */
  get totalCount(): number {
    return this.subscriptions.length;
  }

  /** How many survive the header search — what the table renders. */
  get filteredCount(): number {
    return this.filteredSubscriptions.length;
  }

  /**
   * True when the load came back exactly at its page size, which means the
   * server may well have had more. It is a heuristic, not a fact — a
   * connection with precisely {@link SUBSCRIPTION_PAGE_SIZE} subscriptions
   * trips it — but the failure mode is a caveat on a complete list, which is
   * strictly better than silence on an incomplete one.
   */
  get isAtCap(): boolean {
    return this.subscriptions.length >= SUBSCRIPTION_PAGE_SIZE;
  }

  /** Search matches the event type (raw + friendly label) and the project scope. */
  get filteredSubscriptions(): DevOpsSubscription[] {
    if (!this.searchQuery.trim()) return this.subscriptions;
    const q = this.searchQuery.toLowerCase().trim();
    return this.subscriptions.filter(
      (s) =>
        s.eventType?.toLowerCase().includes(q) ||
        eventTypeLabel(s.eventType).toLowerCase().includes(q) ||
        this.scopeLabel(s).toLowerCase().includes(q)
    );
  }

  /**
   * The rows the table renders: search, then the active column sort (created
   * newest-first by default). Nulls (a row with no recorded project scope)
   * sort last either way.
   */
  get sortedSubscriptions(): DevOpsSubscription[] {
    const key = this.sortKey;
    const valueOf = (s: DevOpsSubscription): SortValue => {
      switch (key) {
        case "eventType":
          // The grid shows the friendly label prominently — sort on it too.
          return textValue(eventTypeLabel(s.eventType));
        case "status":
          return textValue(s.status);
        case "projectScope":
          return textValue(this.scopeLabel(s));
        case "createdAt":
          return dateValue(s.createdAt);
      }
    };
    return sortRows(this.filteredSubscriptions, valueOf, this.sortDir);
  }

  // ---- Filter / sort setters ---------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (created newest-first, text A→Z). */
  setSort(key: DevOpsSubscriptionSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Loading ----------------------------------------------------------------

  /**
   * Entry point from the route. A connection chosen anywhere else (a board,
   * Project 360, last session) is honoured; only an EMPTY scope auto-picks
   * the first connection.
   */
  async initialize() {
    const ended = sessionMark(this.rootStore);
    await this.loadConnections();
    // A sign-out during the read leaves `error` null (it is no failure of the
    // next person's), so the `error` checks below cannot stop the follow-up
    // reads from going out in the new session.
    if (ended()) return;
    // A remembered connection that no longer exists is dropped (with the
    // boards' project) so the auto-pick below runs — never after a FAILED
    // load, which `loadConnections` records on `error`. See
    // PipelinesBoardStore.initialize.
    if (!this.error) {
      this.scopeStore?.reconcileConnections(this.connections.map((c) => c.id));
    }
    if (this.selectedConnectionId) {
      await Promise.all([this.loadSubscriptions(), this.loadProjects()]);
    } else if (this.connections.length > 0) {
      await this.selectConnection(this.connections[0].id);
    }
  }

  async loadConnections() {
    this.connectionsLoading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `connections` reads.
      const result = await this.connectionsService?.find({
        query: { $limit: 200, $sort: { name: 1 } },
      });
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never an org with no connections. It matters twice over here: the
      // cache is empty on a fresh session (`enableStorage: false`), so a
      // silent failure would hand `initialize`'s reconcile an empty list and
      // wipe the remembered scope — the one thing reconcileConnections is
      // documented never to do on a failed load.
      if (result == null) {
        throw swallowedFailure(this.connectionsService, "errorOnFind", "Failed to load connections");
      }
    } catch (err: unknown) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to load connections";
      });

      console.error("Failed to load DevOps connections:", err);
    } finally {
      runInAction(() => {
        this.connectionsLoading = false;
      });
    }
  }

  /**
   * Header selector — writes the shared scope. `setConnection` (not
   * `setScope`) so re-choosing the SAME connection keeps the project the
   * boards are on; a different one clears it, as a project id belongs to
   * one org.
   */
  async selectConnection(connectionId: string | null) {
    runInAction(() => {
      // Invalidate whatever is in flight for the connection being left. When
      // a connection is CHOSEN the loaders below stamp their own sequence, but
      // clearing the selection loads nothing — without this bump the previous
      // connection's answer still matches and paints its projects, or its
      // failure, onto a page that is no longer on any connection.
      this.projectsSeq += 1;
      this.subscriptionsSeq += 1;
      this.projectsLoading = false;
      this.subscriptionsLoading = false;
      this.scopeStore?.setConnection(connectionId);
      this.subscriptions = [];
      this.projects = [];
      // The previous connection's failures are not this one's — neither the
      // picker's nor the list's. Cleared here (not only in the loaders) so
      // that clearing the selection, which loads nothing, cannot leave an
      // alert naming a connection the page is no longer on.
      this.projectsError = null;
      this.error = null;
    });
    if (connectionId) {
      // Subscriptions are the page content; projects only feed the dialog's
      // picker — load both, neither blocks the other.
      await Promise.all([this.loadSubscriptions(), this.loadProjects()]);
    }
  }

  async loadSubscriptions() {
    if (!this.selectedConnectionId) return;
    // Sequence guard, exactly as on loadProjects: connection A's slow answer —
    // a rejection especially — must not land on connection B's healthy list,
    // where it would empty the grid and post an alert naming the wrong org.
    const seq = ++this.subscriptionsSeq;
    this.subscriptionsLoading = true;
    this.error = null;
    try {
      // Connection-scoped + re-queried per selection → read the find() response
      // rows, not the cross-query entity cache.
      // A REAL cap, and one the response cannot disclose: the API's
      // `devops-subscriptions.find` is a direct knex read that applies
      // `Math.min($limit || 200, 500)` and returns a BARE ARRAY — no `total`,
      // so there is nothing to compare against. The only honest signal is that
      // we got back exactly as many rows as we asked for, which is what
      // {@link isAtCap} tests (same heuristic as
      // InstanceActionsStore.auditAtCap). Raising the page size would only make
      // the silence rarer, not honest.
      const result = await this.subscriptionsService?.find({
        query: {
          connectionId: this.selectedConnectionId,
          $limit: SUBSCRIPTION_PAGE_SIZE,
        },
      });
      if (seq !== this.subscriptionsSeq) return; // a newer connection superseded this
      // The wrapper swallows a rejection into null (see
      // shared/swallowedFind.ts). Left as-is that reads as "this connection
      // has no subscriptions": the grid empties, {@link isAtCap} goes quiet
      // and nothing says the read failed. It is a failure, and the previously
      // loaded rows are kept — they are still the last thing the server said.
      if (result == null) {
        throw swallowedFailure(this.subscriptionsService, "errorOnFind", "Failed to load subscriptions");
      }
      runInAction(() => {
        this.subscriptions = rowsOf<DevOpsSubscription>(
          result as unknown as
            | { data?: DevOpsSubscription[] }
            | DevOpsSubscription[],
        );
      });
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a connection
      // this page has left.
      if (seq !== this.subscriptionsSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load subscriptions";
        }
      });

      console.error("Failed to load DevOps subscriptions:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.subscriptionsSeq) this.subscriptionsLoading = false;
      });
    }
  }

  async loadProjects() {
    if (!this.selectedConnectionId) return;
    // Sequence guard (as on the boards' scoped reads): a slow read for the
    // connection the operator has left — a bad PAT that times out — must not
    // land on the connection they moved to. Without it that answer empties
    // the picker and writes ITS failure onto THIS connection, which is a
    // confident sentence about the wrong org.
    const seq = ++this.projectsSeq;
    this.projectsLoading = true;
    // Clear the previous failure as the retry STARTS: leaving it up while the
    // new read is in flight tells the operator the retry has already failed.
    this.projectsError = null;
    try {
      // Live read-only proxy to Azure DevOps via the connection (scoped) →
      // read the find() response rows.
      // No `$limit`: `devops-projects.find` reads only `connectionId` and
      // calls Azure's core.getProjects(), returning whatever page Azure gives
      // back. A `$limit` here was inert — it looked like an enforced cap and
      // was not one. Azure's own paging is the real bound; if an org ever has
      // more projects than one page, the picker needs a search, not a number.
      const result = await this.projectsService?.find({
        query: { connectionId: this.selectedConnectionId },
      });
      // The wrapper swallows a rejection into null. Left as-is that reads as
      // "this connection has no projects": the picker empties and the grid's
      // scope column falls back to raw ids, with nothing saying the lookup
      // failed. It is a failure, and the dialog says so.
      if (seq !== this.projectsSeq) return; // a newer connection superseded this
      if (result == null) {
        throw swallowedFailure(this.projectsService, "errorOnFind", "Failed to load projects");
      }
      runInAction(() => {
        this.projects = rowsOf<DevOpsProject>(
          result as unknown as { data?: DevOpsProject[] } | DevOpsProject[],
        );
      });
    } catch (err: unknown) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      // Project load failures (e.g. bad PAT) must not blank the subscription
      // list — the dialog simply shows an empty picker. Keep the message for
      // the dialog to surface. A SUPERSEDED read reports nothing at all: its
      // failure belongs to a connection this page has left.
      if (seq !== this.projectsSeq) return;
      runInAction(() => {
        this.projects = [];
        this.projectsError =
          err instanceof Error ? err.message : "Failed to load projects";
      });

      console.error("Failed to load DevOps projects:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner — an old one clearing it would
        // report the new read as finished.
        if (seq === this.projectsSeq) this.projectsLoading = false;
      });
    }
  }

  /** Header refresh — re-pull subscriptions for the current selection. */
  async reload() {
    if (this.selectedConnectionId) {
      await this.loadSubscriptions();
    } else {
      await this.initialize();
    }
  }

  // ---- Create form -------------------------------------------------------------

  /**
   * Seed the Add Subscription form. The project picker starts on the shared
   * scope's project when the picker lists it (the operator came from a board
   * or Project 360 looking at that project); it stays editable.
   */
  startCreate() {
    this.formError = null;
    const scoped = this.scopeStore?.projectId ?? null;
    this.formData = {
      eventType: "",
      projectId: scoped && this.projects.some((p) => p.id === scoped) ? scoped : null,
    };
  }

  setFormField(
    field: keyof DevOpsSubscriptionFormData,
    value: string | null
  ) {
    this.formData = {
      ...this.formData,
      [field]: value,
    } as DevOpsSubscriptionFormData;
  }

  get isFormValid(): boolean {
    return !!this.selectedConnectionId && !!this.formData.eventType?.trim();
  }

  /**
   * Create the subscription (local row + remote Azure service hook). Project
   * scoping goes through `publisherInputs.projectId` from the picker — the
   * API forwards `publisherInputs` verbatim to Azure's subscription create.
   * Returns false (and sets `formError`) on failure so the dialog can stay
   * open and surface API errors (bad PAT / missing service-hook permissions).
   */
  async saveSubscription(): Promise<boolean> {
    if (!this.isFormValid || !this.selectedConnectionId) {
      this.formError = "An event type is required.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    const ended = sessionMark(this.rootStore);
    try {
      const payload: DevOpsSubscriptionCreatePayload = {
        connectionId: this.selectedConnectionId,
        eventType: this.formData.eventType!.trim(),
      };
      if (this.formData.projectId) {
        payload.publisherInputs = { projectId: this.formData.projectId };
        // The picker's name rides along (the API stores it as the row's
        // devopsProjectName); without it the row is a GUID with no name and
        // the overview lists the project as `name: null`.
        const projectName = this.projects.find((p) => p.id === this.formData.projectId)?.name;
        if (projectName) payload.projectName = projectName;
      }
      // THROW_ON_ERROR: a refusal otherwise RESOLVES null — the dialog closed
      // as if the hook existed and the Azure error never showed.
      await this.subscriptionsService?.create(payload as never, THROW_ON_ERROR);

      await this.loadSubscriptions();
      // Created; the reload swallows a sign-out, and the form is reset already.
      if (ended()) return true;
      runInAction(() => {
        this.formData = {};
      });
      return true;
    } catch (err: unknown) {
      // A sign-out: no error to show, and no refresh — it would go out in the
      // next person's session.
      if (isSessionEnded(err)) return false;
      // Surface the API error (e.g. "Failed to create Azure service hook
      // subscription: …" on a bad PAT / insufficient permissions) in the dialog.
      runInAction(() => {
        this.formError =
          err instanceof Error ? err.message : "Failed to create subscription";
      });
      // A failed remote create still leaves a local 'failed' audit row — refresh
      // so the grid reflects it.
      await this.loadSubscriptions();

      console.error("saveSubscription failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** Open the delete confirmation dialog for a subscription. */
  requestRemove(subscription: DevOpsSubscription) {
    this.confirmTarget = subscription;
  }

  /** Dismiss the delete confirmation dialog. */
  cancelRemove() {
    this.confirmTarget = null;
  }

  /** Confirm-dialog action: remove the pending subscription. */
  async executeConfirmedRemove() {
    const target = this.confirmTarget;
    if (!target) return;
    this.confirmTarget = null;
    await this.removeSubscription(target.id);
  }

  /**
   * Remove a subscription — soft-deletes the local row AND deletes the remote
   * Azure hook (best-effort on the API side). Operator/admin only.
   */
  async removeSubscription(id: string) {
    this.removingId = id;
    this.error = null;
    try {
      await this.subscriptionsService?.remove(id, THROW_ON_ERROR);
      await this.loadSubscriptions();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to remove subscription";
        }
      });

      console.error("Failed to remove subscription:", err);
    } finally {
      runInAction(() => {
        this.removingId = null;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.subscriptions = [];
    this.projects = [];
    this.connectionsLoading = false;
    this.subscriptionsLoading = false;
    this.projectsLoading = false;
    this.projectsError = null;
    this.error = null;
    this.projectsSeq += 1;
    this.subscriptionsSeq += 1;
    this.searchQuery = "";
    this.sortKey = "createdAt";
    this.sortDir = "desc";
    this.formData = {};
    this.saving = false;
    this.formError = null;
    this.removingId = null;
    this.confirmTarget = null;
  }
}

export default DevOpsSubscriptionsStore;
